import { SyntheticEvent, useEffect, useState } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom'; // allows us to get campaign id from URL
import { TabsContainer } from '../../../components/Tabs';
import ApiService from '../../../ApiService';
import AppBreadcrumbs from '../../../components/AppBreadcrumbs';
import Block from '../../../components/Block';
import Column from '../../../components/Column';
import Heading from '../../../components/Heading';
import Row from '../../../components/Row';
import StatusDate from '../../../components/StatusDate';
import TabPanel from '../../../components/TabPanel';
import { Campaign, CampaignFee } from '../../../types/Campaign';
import { CampaignDetailsPageContext } from '../../../hooks/useCampaignDetailsPage';
import { useUserContext } from '../../../hooks/Contexts/UserContext';
import useCampaignDetailsPage from '../../../hooks/useCampaignDetailsPage';
import AdServerPlacementsTab from './AdServerPlacementsTab';
import CampaignArchiveButton from './CampaignArchiveButton';
import CampaignDeleteButton from './CampaignDeleteButton';
import CampaignDetailsHeader from './CampaignDetailsHeader';
import CampaignFeesComponent from './CampaignFeesComponent';
import CampaignModifyButton from './CampaignModifyButton';

import ChangeRequestTab from './ChangeRequestTab';
import CreativeTab from './CreativeTab';
import FlightsTab from './FlightsTab';
import PerformanceTab from './PerformanceTab';
import SummaryTab from './SummaryTab';
import TargetingTab from './TargetingTab';
import usePageTitle from '../../../hooks/usePageTitle';

const FT_SAS_ACCOUNT_ID = 2;
const VIDEO_MEDIA_TYPE = 'Video';

const TAB_CONFIG = {
    performance: { label: 'Performance', slug: 'performance' },
    summary: { label: 'Summary', slug: 'summary' },
    targeting: { label: 'Targeting', slug: 'targeting' },
    creative: { label: 'Creative', slug: 'creative' },
    flights: { label: 'Flights', slug: 'flights' },
    'ad-server-placements': { label: 'Ad Server Placements', slug: 'ad-server-placements' },
    fees: { label: 'Fees', slug: 'fees' },
    'change-requests': { label: 'Change Requests', slug: 'change-requests' },
} as const;

type TabSlug = keyof typeof TAB_CONFIG;

function isTabSlug(value: string): value is TabSlug {
    return value in TAB_CONFIG;
}

const DEFAULT_TAB_ID = TAB_CONFIG['performance'].slug;

const CampaignDetailsPage = (): React.JSX.Element | null => {
    // const location = useLocation();
    const { id: idParameter, tab: tabParameter } = useParams();
    const navigate = useNavigate();
    const id = idParameter ? +idParameter : null; // grab id from url params
    const campaignDetailsPage = useCampaignDetailsPage();
    const { campaignChangeRequest } = campaignDetailsPage;

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const { userContext } = useUserContext();
    const initialTabId = tabParameter && isTabSlug(tabParameter) ? tabParameter : DEFAULT_TAB_ID;
    const [selectedTab, setSelectedTab] = useState<TabSlug>(initialTabId);

    // TITLE CREATION
    const campaignName = campaign?.campaignName ? `Campaign "${campaign.campaignName}"` : null;
    const segments = [TAB_CONFIG[selectedTab].label];

    usePageTitle({ entity: campaignName, segments });

    useEffect(() => {
        getCampaign();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id || !tabParameter) return;

        // invalid tab slug; redirect
        if (!isTabSlug(tabParameter)) {
            navigate(`/campaignManager/${id}/${DEFAULT_TAB_ID}`, { replace: true });
        } else {
            // Sync selectedTab state with URL parameter
            setSelectedTab(tabParameter);
        }
    }, [id, tabParameter, navigate]);

    function getCampaign() {
        if (id) {
            ApiService.getCampaign(id)
                .then((response) => {
                    let campaign = response.data;
                    if (campaign.deliveryStatusId === 'PENDING_COGNITION' || campaign.hasUpdates) {
                        setSelectedTab(DEFAULT_TAB_ID);
                        navigate(`/campaignManager/${id}/${DEFAULT_TAB_ID}`, { replace: false });
                    }
                    setCampaign(campaign);
                })
                .catch((error) => {
                    if (error?.response?.status === 404) {
                        navigate('/notFound');
                    }
                });
        }
    }

    const showAdServerPlacements = () =>
        campaign?.dealer?.sasAccountId === FT_SAS_ACCOUNT_ID &&
        campaign?.dealer?.ftAdvertiserId &&
        userContext.isAdmin() &&
        campaign?.mediaType === VIDEO_MEDIA_TYPE;

    function selectTab(_event: SyntheticEvent, value: TabSlug) {
        setSelectedTab(value);

        if (id != null) navigate(`/campaignManager/${id}/${value}`);
    }

    if (!campaign) {
        return null;
    }

    return (
        <CampaignDetailsPageContext.Provider value={campaignDetailsPage}>
            <Column gap={0} flex={1}>
                <Block>
                    <Column gap={1}>
                        <AppBreadcrumbs items={[{ label: 'Campaigns', href: '/campaignManager' }]} />
                        <Row gap={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <span>
                                <Heading>Campaign Details</Heading>
                            </span>
                            <Column flex={0} gap={1}>
                                <Row gap={1}>
                                    <CampaignModifyButton
                                        campaign={campaign}
                                        campaignChangeRequest={campaignChangeRequest}
                                    />
                                    {(userContext.isAdmin() || userContext.isAgency()) && (
                                        <CampaignArchiveButton
                                            campaign={campaign}
                                            onCampaignArchive={() => {
                                                setTimeout(() => {
                                                    navigate('/campaignManager');
                                                }, 2000);
                                            }}
                                        />
                                    )}
                                    {userContext.isAdmin() && (
                                        <CampaignDeleteButton
                                            campaign={campaign}
                                            onCampaignDelete={() => {
                                                setTimeout(() => {
                                                    navigate('/campaignManager');
                                                }, 2000);
                                            }}
                                        />
                                    )}
                                </Row>
                                {!!campaign?.dspCampaignSyncedDate && (
                                    <Row>
                                        <StatusDate title="Last synced" date={campaign.dspCampaignSyncedDate} />
                                    </Row>
                                )}
                                {!!campaign?.dspCampaignRefreshedDate && (
                                    <Row>
                                        <StatusDate
                                            title="Last refresh called"
                                            date={campaign.dspCampaignRefreshedDate}
                                        />
                                    </Row>
                                )}
                            </Column>
                        </Row>
                    </Column>
                </Block>
                <Column gap={2} sx={{ height: '100%' }}>
                    <CampaignDetailsHeader campaign={campaign} />

                    <TabsContainer>
                        <Tabs value={selectedTab} onChange={selectTab}>
                            <Tab value={TAB_CONFIG.performance.slug} label={TAB_CONFIG.performance.label} />
                            <Tab value={TAB_CONFIG.summary.slug} label={TAB_CONFIG.summary.label} />
                            <Tab value={TAB_CONFIG.targeting.slug} label={TAB_CONFIG.targeting.label} />
                            <Tab value={TAB_CONFIG.creative.slug} label={TAB_CONFIG.creative.label} />

                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <Tab value={TAB_CONFIG.flights.slug} label={TAB_CONFIG.flights.label} />
                            )}

                            {showAdServerPlacements() && (
                                <Tab
                                    value={TAB_CONFIG['ad-server-placements'].slug}
                                    label={TAB_CONFIG['ad-server-placements'].label}
                                />
                            )}

                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <Tab value={TAB_CONFIG.fees.slug} label={TAB_CONFIG.fees.label} />
                            )}

                            <Tab
                                value={TAB_CONFIG['change-requests'].slug}
                                label={TAB_CONFIG['change-requests'].label}
                            />
                        </Tabs>
                    </TabsContainer>

                    {/* TAB, RENDERED ----------------------------------------------------------------------- */}
                    {/* TabPanel will hide the tab if index !== valuen  */}
                    <Block>
                        <TabPanel index={TAB_CONFIG.performance.slug} value={selectedTab}>
                            <PerformanceTab campaign={campaign} />
                        </TabPanel>
                        <TabPanel index={TAB_CONFIG.summary.slug} value={selectedTab}>
                            <SummaryTab campaign={campaign} reloadCampaign={getCampaign} />
                        </TabPanel>
                        <TabPanel index={TAB_CONFIG.targeting.slug} value={selectedTab}>
                            <TargetingTab campaign={campaign} onChange={() => {}} />
                        </TabPanel>
                        {userContext.isAdmin() && (
                            <TabPanel index={TAB_CONFIG.creative.slug} value={selectedTab}>
                                <CreativeTab campaign={campaign} />
                            </TabPanel>
                        )}
                        {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                            <TabPanel index={TAB_CONFIG.flights.slug} value={selectedTab}>
                                <FlightsTab campaign={campaign} reloadCampaign={getCampaign} />
                            </TabPanel>
                        )}
                        <TabPanel index={TAB_CONFIG['ad-server-placements'].slug} value={selectedTab}>
                            <AdServerPlacementsTab campaign={campaign} reloadCampaign={getCampaign} />
                        </TabPanel>
                        {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                            <TabPanel index={TAB_CONFIG.fees.slug} value={selectedTab}>
                                <CampaignFeesComponent
                                    campaignFee={campaign.campaignFee}
                                    allowEditToggle={
                                        (userContext.isAdmin() || userContext.isOpsAdmin()) &&
                                        campaign.deliveryStatusId !== 'ENDED'
                                    }
                                    editMode={false}
                                    onChange={(campaignFee: CampaignFee) => {
                                        const updatedCampaign = { ...campaign, campaignFee: campaignFee };
                                        setCampaign(updatedCampaign);
                                        ApiService.updateCampaign(updatedCampaign).then((response) => {
                                            setCampaign(response.data);
                                        });
                                    }}
                                />
                            </TabPanel>
                        )}
                        <TabPanel index={TAB_CONFIG['change-requests'].slug} value={selectedTab}>
                            <ChangeRequestTab campaign={campaign} />
                        </TabPanel>
                    </Block>
                </Column>
            </Column>
        </CampaignDetailsPageContext.Provider>
    );
};
export default CampaignDetailsPage;
